<template>
    <div class="add-venue-address-form">
        <form @submit.prevent="onSubmit" class="new-address-form">
            <p class="title">Add New Venue</p>
            <div class="first-row">
                <label for="venue-name">What's the venue's name?</label>
                <input
                    ref="autocomplete"
                    v-model="$v.formData.venue_name.$model"
                    @keydown="$v.formData.venue_name.$touch"
                    type="text"
                    name="venue-name"
                    class="venue-name"
                    placeholder="Add venue name"
                />
                <p
                    v-if="$v.formData.venue_name.$dirty && !$v.formData.venue_name.required"
                    class="error-message"
                >
                    Please enter a valid venue name.
                </p>
            </div>
            <div class="second-row">
                <label for="venue-address">What's the venue's address?</label>
                <input
                    ref="autocompleteAddress"
                    v-model="$v.formData.address.$model"
                    @keydown="$v.formData.address.$touch"
                    @keydown.enter="preventSubmit"
                    type="text"
                    name="venue-address"
                    class="venue-address"
                    placeholder="Add venue address"
                />
                <p
                    v-if="$v.formData.address.$dirty && !$v.formData.address.required"
                    class="error-message"
                >
                    Please select from a valid address.
                </p>
            </div>
            <div class="action-row">
                <button type="button" class="cancel-btn" @click="$emit('cancel-form')">
                    Cancel
                </button>
                <button :disabled="$v.$invalid" type="submit" class="save-btn">Save</button>
            </div>
        </form>
    </div>
</template>
<script>
    import { ref, watchEffect } from 'vue'
    import { required } from 'vuelidate/lib/validators'
    import { useValidation } from '@/composables/utils/useValidation'
    import { useGoogleAPI } from '@/composables/utils/useGoogleAPI'

    export default {
        name: 'AddVenueAddress',
        emits: ['form-submit', 'cancel-form'],
        setup() {
            const autocomplete = ref(null)
            const autocompleteAddress = ref(null)
            const { apiLoaded, initialize, lat, lng, fullAddress, business } = useGoogleAPI()
            const { isValid } = useValidation()

            function _initAutocompleteData(templateRef, types) {
                autocompleteAddress.value = new google.maps.places.Autocomplete(templateRef, {
                    fields: ['geometry', 'formatted_address'],
                    types: types ? types : ['geocode', 'establishment'],
                    componentRestrictions: { country: 'us' }
                })

                autocompleteAddress.value.addListener('place_changed', _handlePlaceChanged)
            }
            function _handlePlaceChanged() {
                const place = autocompleteAddress.value.getPlace()

                if (place.geometry && place.formatted_address) {
                    lat.value = place.geometry.location.lat()
                    lng.value = place.geometry.location.lng()
                    fullAddress.value = place.formatted_address
                }
            }

            watchEffect(() => {
                const googleRef = autocomplete.value

                if (apiLoaded.value && googleRef) {
                    initialize({
                        mode: 'autocomplete',
                        templateRef: googleRef
                    })
                }
                if (apiLoaded.value && autocompleteAddress.value) {
                    _initAutocompleteData(autocompleteAddress.value, [
                        'geocode',
                        'establishment'
                    ])
                }
            })

            return {
                autocomplete,
                autocompleteAddress,
                apiLoaded,
                initialize,
                lat,
                lng,
                fullAddress,
                business,

                isValid
            }
        },
        data: () => ({
            formData: {
                venue_name: '',
                address: '',
                lat: null,
                lon: null
            }
        }),
        validations: {
            formData: {
                venue_name: {
                    required
                },
                address: {
                    required
                }
            }
        },
        watch: {
            fullAddress: function (address) {
                this.formData.address = address
            },
            lat: function (lat) {
                this.formData.lat = lat
            },
            lng: function (lng) {
                this.formData.lon = lng
            },
            business: function (business) {
                this.formData.venue_name = business
            }
        },
        methods: {
            preventSubmit(e) {
                if (e.keyCode == '13') {
                    e.preventDefault()
                }
            },
            onSubmit() {
                if (this.isValid(this.$v)) {
                    this.$emit('form-submit', this.formData)
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../../../sass/_variables.scss';
    .add-venue-address-form {
        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        max-width: 494px;
        width: 100%;

        .new-address-form {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 16px;

            .title {
                color: #dedede;
                font-family: 'Instrument Sans';
                font-size: 26px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.52px;
            }

            .first-row,
            .second-row {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 6px;

                label {
                    color: #acacac;
                    font-family: 'Instrument Sans';
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 135%;
                    letter-spacing: 0.12px;
                }

                input {
                    width: 100%;
                    padding: 12px 15px 15px 15px;
                    border-radius: 12px;
                    border: 1.5px solid #8b8b8b;
                    background: #262626;
                    color: #dedede;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                }
                input::placeholder {
                    overflow: hidden;
                    color: #646464;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                }
                .error-message {
                    color: #ed3c0d;
                    font-family: Instrument Sans;
                    font-size: 10px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 130%;
                    letter-spacing: 0.14px;
                }
            }
            .action-row {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 24px;

                .cancel-btn {
                    background: none;
                    color: #acacac;
                    text-align: center;
                    font-family: 'Instrument Sans';
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                    text-decoration-line: underline;
                }
                .save-btn {
                    border-radius: 120px;
                    padding: 8px 16px;
                    background: #cdeb09;
                    color: #262626;
                    font-family: 'Instrument Sans';
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 115%;
                    letter-spacing: 0.14px;

                    &:disabled {
                        background: #464646;
                        cursor: not-allowed;
                    }
                }
            }
        }
    }
</style>
